import React, { useEffect, useMemo, useState } from "react";
import { bulkUpsertCompetitionParticipants, getListUsers, userFullName, userShortName } from "../../../api";
import { Button } from "../Button";
import { UserSelect } from "../form/UserSelect";
import { ID, TournamentParticipant, User } from "../../../types";

interface ParticipantsManagerProps {
  participants: TournamentParticipant[];
  eventId: ID;
  seasonId: ID;
  canManage: boolean;
  onChanged: () => Promise<void> | void;
}

interface ParticipantFormState {
  name: string;
  players: Array<ID | null>;
}

interface ParticipantDraft {
  tempId: string;
  id?: ID;
  event: ID;
  name: string;
  players: ID[];
  seed: number | null;
}

const getEmptyForm = (): ParticipantFormState => ({
  name: "",
  players: [null],
});

const buildAutoName = (players: ID[], usersById: Map<ID, User>) => {
  if (!players.length) return "Nuovo Team";
  return players
    .map((playerId) => {
      const user = usersById.get(playerId);
      // Nome compatto centralizzato (es. "Rossi M."), coerente con la vista mobile.
      return user ? userShortName(user) : "";
    })
    .filter(Boolean)
    .join(" - ");
};

const mapParticipantToDraft = (participant: TournamentParticipant): ParticipantDraft => ({
  tempId: String(participant.id),
  id: participant.id,
  event: participant.event,
  name: participant.name,
  players: participant.players ?? [],
  seed: participant.seed ?? null,
});

interface ParticipantModalProps {
  open: boolean;
  users: User[];
  selectedUsers: ID[];
  title: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (payload: { name: string; players: ID[] }) => void;
  initial?: ParticipantFormState;
}

const ParticipantModal: React.FC<ParticipantModalProps> = ({
  open,
  users,
  selectedUsers,
  title,
  confirmLabel,
  onClose,
  onConfirm,
  initial,
}) => {
  const [form, setForm] = useState<ParticipantFormState>(initial ?? getEmptyForm());

  useEffect(() => {
    if (open) {
      setForm(initial ?? getEmptyForm());
    }
  }, [open, initial]);

  const addPlayerSlot = () => {
    setForm((prev) => ({ ...prev, players: [...prev.players, null] }));
  };

  const removePlayerSlot = (index: number) => {
    setForm((prev) => {
      const nextPlayers = prev.players.filter((_, i) => i !== index);
      return { ...prev, players: nextPlayers.length ? nextPlayers : [null] };
    });
  };

  const updatePlayerSlot = (index: number, value: ID | null) => {
    setForm((prev) => {
      const nextPlayers = [...prev.players];
      nextPlayers[index] = value;
      return { ...prev, players: nextPlayers };
    });
  };

  const handleConfirm = () => {
    const selectedPlayers = form.players.filter((p): p is ID => p != null);
    if (selectedPlayers.length < 1) {
      alert("Seleziona almeno un player.");
      return;
    }
    if (new Set(selectedPlayers).size !== selectedPlayers.length) {
      alert("Ogni player può essere selezionato una sola volta.");
      return;
    }
    onConfirm({
      name: form.name,
      players: selectedPlayers,
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div className="bg-white rounded-t-xl w-full max-w-md p-6 animate-slideUp">
        <h3 className="text-lg font-bold mb-4">{title}</h3>
        <div className="grid grid-cols-1 gap-3">
          <label className="block text-sm">
            Nome
            <input
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Es. Team Rossi"
            />
          </label>
          <div className="text-sm">
            <div className="flex items-center justify-between">
              <span>Players</span>
              <Button size="sm" variant="success" onClick={addPlayerSlot} disabled={form.players.length >= users.length}>
                +
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.players.map((playerId, index) => (
                <div key={index} className="flex items-center gap-2">
                  {index > 0 && (
                    <Button size="sm" variant="destructive" onClick={() => removePlayerSlot(index)}>
                      -
                    </Button>
                  )}
                  <UserSelect
                    users={users}
                    value={playerId}
                    placeholder="Seleziona player"
                    emptyOptionLabel="Seleziona player"
                    disabledUserIds={selectedUsers}
                    onChange={(userId) => updatePlayerSlot(index, userId)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between space-x-2 mt-4">
          <Button variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ParticipantCreateModalProps {
  open: boolean;
  users: User[];
  selectedUsers: ID[];
  onClose: () => void;
  onCreate: (payload: { name: string; players: ID[] }) => void;
}

const ParticipantCreateModal: React.FC<ParticipantCreateModalProps> = ({
  open,
  users,
  selectedUsers,
  onClose,
  onCreate,
}) => {
  return (
    <ParticipantModal
      open={open}
      users={users}
      selectedUsers={selectedUsers}
      title="Nuovo partecipante"
      confirmLabel="Aggiungi"
      onClose={onClose}
      onConfirm={onCreate}
    />
  );
};

interface ParticipantEditModalProps {
  open: boolean;
  users: User[];
  selectedUsers: ID[];
  participant: ParticipantDraft | null;
  onClose: () => void;
  onSave: (payload: { name: string; players: ID[] }) => void;
}

const ParticipantEditModal: React.FC<ParticipantEditModalProps> = ({
  open,
  users,
  selectedUsers,
  participant,
  onClose,
  onSave,
}) => {
  const initial: ParticipantFormState | undefined = participant
    ? { name: participant.name, players: participant.players.length ? participant.players : [null] }
    : undefined;

  return (
    <ParticipantModal
      open={open}
      users={users}
      selectedUsers={selectedUsers}
      title="Modifica partecipante"
      confirmLabel="Aggiorna"
      onClose={onClose}
      onConfirm={onSave}
      initial={initial}
    />
  );
};

export const ParticipantsManager: React.FC<ParticipantsManagerProps> = ({
  participants,
  eventId,
  seasonId,
  canManage,
  onChanged,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [drafts, setDrafts] = useState<ParticipantDraft[]>(participants.map(mapParticipantToDraft));
  const [deletedIds, setDeletedIds] = useState<ID[]>([]);
  const [savingBulk, setSavingBulk] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ParticipantDraft | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      const data = await getListUsers(Number(seasonId));
      setUsers(data);
    };
    fetchUsers();
  }, [seasonId]);

  useEffect(() => {
    setDrafts(participants.map(mapParticipantToDraft));
    setDeletedIds([]);
  }, [participants]);

  const usersById = useMemo(() => {
    return new Map(users.map((u) => [u.id, u]));
  }, [users]);

  const hasPendingChanges = useMemo(() => {
    if (deletedIds.length) return true;
    if (drafts.length !== participants.length) return true;
    const byId = new Map(participants.map((p) => [p.id, p]));
    return drafts.some((draft) => {
      if (!draft.id) return true;
      const existing = byId.get(draft.id);
      if (!existing) return true;
      if (existing.name !== draft.name) return true;
      if ((existing.seed ?? null) !== (draft.seed ?? null)) return true;
      const existingPlayers = (existing.players ?? []).join(",");
      const draftPlayers = (draft.players ?? []).join(",");
      return existingPlayers !== draftPlayers;
    });
  }, [deletedIds, drafts, participants]);

  const handleCreate = (payload: { name: string; players: ID[] }) => {
    const nextName = payload.name.trim() || buildAutoName(payload.players, usersById);
    const tempId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setDrafts((prev) => [
      ...prev,
      {
        tempId,
        event: eventId,
        name: nextName,
        players: payload.players,
        seed: null,
      },
    ]);
    setCreateOpen(false);
  };

  const handleEdit = (payload: { name: string; players: ID[] }) => {
    if (!editTarget) return;
    const nextName = payload.name.trim() || buildAutoName(payload.players, usersById);
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.tempId === editTarget.tempId
          ? { ...draft, name: nextName, players: payload.players }
          : draft
      )
    );
    setEditTarget(null);
  };

  const handleDelete = (participant: ParticipantDraft) => {
    if (!canManage) return;
    const confirmDelete = window.confirm("Eliminare questo partecipante?");
    if (!confirmDelete) return;
    setDrafts((prev) => prev.filter((item) => item.tempId !== participant.tempId));
    if (participant.id) {
      setDeletedIds((prev) => [...prev, participant.id as ID]);
    }
  };

  const handleSeedChange = (participant: ParticipantDraft, value: string) => {
    const parsed = value.trim() === "" ? null : Number(value);
    const seed = Number.isNaN(parsed) ? null : parsed;
    setDrafts((prev) =>
      prev.map((item) =>
        item.tempId === participant.tempId ? { ...item, seed } : item
      )
    );
  };

  const handleBulkSave = async () => {
    if (!canManage || savingBulk) return;

    setSavingBulk(true);
    try {
      const payloadParticipants = drafts.map((draft) => {
        const name = draft.name.trim() || buildAutoName(draft.players, usersById);
        if (draft.players.length < 1) {
          throw new Error(`Il partecipante "${name}" deve avere almeno un player.`);
        }
        if (new Set(draft.players).size !== draft.players.length) {
          throw new Error(`Il partecipante "${name}" contiene player duplicati.`);
        }
        return {
          id: draft.id,
          event: eventId,
          name,
          players: draft.players,
          seed: draft.seed ?? null,
        };
      });
      await bulkUpsertCompetitionParticipants({
        event: eventId,
        participants: payloadParticipants,
        deleted_ids: deletedIds,
      });
      await onChanged();
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert("Errore nel salvataggio dei partecipanti.");
      }
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <div className="bg-white border border-gray-300 rounded shadow-sm p-6 mt-6">
      <div className="">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Partecipanti</h2>
          <i className="text-sm text-gray-600 mt-auto">{drafts.length} registrati</i>
        </div>
        {canManage && (
          <div className="flex items-center justify-end gap-4 border-b py-2 mb-4">
            <div className="flex items-center gap-3">
                <Button size="sm" variant="success" onClick={() => setCreateOpen(true)}>
                  <span className="sm:hidden">Nuovo</span>
                  <span className="hidden sm:inline">Nuovo partecipante</span>
                </Button>
            </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={handleBulkSave} disabled={!hasPendingChanges || savingBulk}>
                  Salva
                </Button>
              </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {drafts.map((participant) => (
          <div key={participant.tempId} className="border border-gray-300 bg-white rounded p-4" >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-sm">{participant.name}</div>
                { participant.players.length > 1 && (
                  <i className="mt-1 text-xs block text-gray-600">
                    {(participant.players || [])
                      .map((id) => usersById.get(id))
                      .filter(Boolean)
                      .map((u) => (u ? userFullName(u) : ""))
                      .join(", ") || "—"}
                  </i>
                )}
              </div>
              {canManage ? (
                  <div>
                    <input
                      type="number"
                      className="border border-gray-300 rounded px-2 py-1 max-w-[3.5rem] md:max-w-[6rem] text-sm"
                      size={2}
                      min={0}
                      step={1}
                      value={participant.seed ?? ""}
                      onChange={(e) => handleSeedChange(participant, e.target.value)}
                      placeholder="Peso"
                    />
                  </div>
              ) : (
                <div className="text-sm">{participant.seed ?? ""}</div>
              )}
            </div>
              {canManage && (
                <div className="flex items-center justify-between gap-2 mt-2">
                  <div>
                    <Button size="sm" variant="warning" onClick={() => setEditTarget(participant)}>
                      <span>Modifica</span>
                    </Button>
                  </div>
                  <div>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(participant)}>
                      <span>Rimuovi</span>
                    </Button>
                  </div>
                </div>
              )}
          </div>
        ))}

        {drafts.length === 0 && (
          <div className="py-4 text-center text-gray-500">Nessun partecipante inserito.</div>
        )}
      </div>

      <ParticipantCreateModal
        open={createOpen}
        users={users}
        selectedUsers={drafts.flatMap((d) => d.players)}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />
      <ParticipantEditModal
        open={!!editTarget}
        users={users}
        selectedUsers={drafts.flatMap((d) => d.players).filter((id) => !editTarget?.players.includes(id))}
        participant={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleEdit}
      />
    </div>
  );
};
